import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import { getCurrentLocale } from 'src/helper/helper';
import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';

import type { OrgUnitResponseRate } from './helpers';
import { getResponseRateForOrgUnit } from './helpers';

type UseExportOptionsProps = {
  parentOrgUnit: OrgUnitResponseRate | null;
  orgUnits: OrgUnitResponseRate[] | null;
  parentOrgUnitComparison: OrgUnitResponseRate | null;
  orgUnitsComparison: OrgUnitResponseRate[] | null;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

export const useExportOptions = ({
  parentOrgUnit,
  orgUnits,
  widgetName,
  queryFilters,
  parentOrgUnitComparison,
  orgUnitsComparison,
}: UseExportOptionsProps): WidgetExportOptions => {
  const { t } = useTranslation('followUp');
  const locale = getCurrentLocale();

  const headerRow: Row = [
    { value: t('statistics.orgUnit'), fontWeight: 'bold' },
    { value: t('statistics.invitees'), fontWeight: 'bold' },
    { value: t('statistics.respondents'), fontWeight: 'bold' },
    { value: t('statistics.responseRate'), fontWeight: 'bold' },
  ];
  return useMemo(() => {
    const rows: Row[] = [
      headerRow,
      ...[...(parentOrgUnit ? [parentOrgUnit] : []), ...(orgUnits ?? [])].map(
        (orgUnit) =>
          [
            {
              value: orgUnit.label,
            },
            {
              value: orgUnit.invitees,
            },
            {
              value: orgUnit.respondents,
            },
            {
              value: `${getResponseRateForOrgUnit(orgUnit, locale)}%`,
              align: 'right',
            },
          ] satisfies Row,
      ),
      parentOrgUnitComparison || orgUnitsComparison ? headerRow : [],
      ...[
        ...(parentOrgUnitComparison ? [parentOrgUnitComparison] : []),
        ...(orgUnitsComparison ?? []),
      ].map(
        (orgUnit) =>
          [
            {
              value: orgUnit.label,
            },
            {
              value: orgUnit.invitees,
            },
            {
              value: orgUnit.respondents,
            },
            {
              value: `${getResponseRateForOrgUnit(orgUnit, locale)}%`,
              align: 'right',
            },
          ] satisfies Row,
      ),
    ];

    return {
      widgetName,
      rows,
      queryFilters,
    };
  }, [
    t,
    locale,
    parentOrgUnit,
    orgUnits,
    widgetName,
    queryFilters,
    parentOrgUnitComparison,
    orgUnitsComparison,
  ]);
};
